/* global Coloris, tingle, Croppie */
class VKCustomizableProductSection extends HTMLElement {
  constructor() {
    super();
    this.config = window.vkcf;
    this.canvas = document.querySelector(`#VKCanvas-${this.dataset.section}`);
    this.shareButton = document.querySelector(`#ProductShareButton-${this.dataset.section}`);
    this.fieldset = this.querySelector('fieldset');
    this.block = this.getBlockSettings(this.fieldset);
    this.textarea = this.querySelector('textarea');
    this.imageUpload = this.querySelector(`#${this.fieldset.dataset.block}-image-upload`);
    this.bgImageUpload = this.querySelector(`#${this.fieldset.dataset.block}-bgImage-upload`);
    this.bgImageUploadCrop = this.querySelector(`#${this.fieldset.dataset.block}-bgImage-upload-crop`);
    this.bgImageCropped = this.querySelector(`#${this.fieldset.dataset.block}-bgImage-cropped`);
    this.qrUrlField = this.querySelector(`#${this.fieldset.dataset.block}-qr-url`);
    this.tabsController = this.querySelector('.tab-control'); // remove
    this.dropdownControllers = this.querySelectorAll('.dropdown-controller');
    this.typeController = this.querySelector('.vkcf-controls .input-type');

    this.textSizeController = this.querySelector('.vkcf-controls [data-control="text-size"]');
    this.textAlignController = this.querySelector('.vkcf-controls [data-control="text-align"]');
    this.paletteController = this.querySelector('.vkcf-controls [data-control="palette"]');
    this.scriptController = this.querySelector('.vkcf-controls .script');
    this.emojiController = this.querySelector('.vkcf-controls .emoji-selector');
    this.emojiWrapper = this.querySelector('.vkcf-controls .emoji-selector .emoji-wrapper');
    this.preRenderText = document.querySelector('.vk-font-preload .pre-render');
    this.selectedType = null;
    this.selectedSize = null;
    this.selectedAlignment = null;
    this.selectedBgColor = null;
    this.maxLines = 0;
    this.lineCharacterLimit = 0;
    this.specialChars = ['♦', '♥', '❤', '…', '“', '”', '‘', '’', '—']; // ["♦", "♥", "❤️", "😀"]; ちは

    // We need to uncomment the font preload on vk-font-preload-dependencies.liquid and vk-custom-fonts.css.liquid
    this.emojis = [
      '😂', // - U+1F602
      '😭', // - U+1F62D
      '❤', // - U+2764
      '🤣', // - U+1F923
      '🔥', // - U+1F525
      '😍', // - U+1F60D
      '🥺', // - U+1F97A
      '🥰', // - U+1F970
      '🙏', // - U+1F64F
      '✨', // - U+2728
      '👎', // - U+1F44E
      '👍', // - U+1F44D
      '👏', // - U+1F44F
      '🤷', // - U+1F937
      '🫠', // - U+1FAE0
      '🤔', // - U+1F914
      '🙄', // - U+1F644
      '😐', // - U+1F610
      '😉', // - U+1F609
      '🙃', // - U+1F643
    ];

    this.rtlAllowed = this.block.options.rtlAllowed ?? true;
    this.selectedScript = 'ltr';

    this.allowEmojis = this.block.options.emojis || false;
    this.allowSharing = this.block.options.allowSharing || false;
    this.watermark = this.block.options.overlayWatermarkUrl || null;
    this.allowedSpecialChars = [...this.specialChars, ...(this.allowEmojis ? this.emojis : [])];

    this.allowedLanguages = [
      { lang: 'base', script: ['Latin', 'Common'] },
      { lang: 'cyrillic', script: ['Cyrillic'] },
      { lang: 'el', script: ['Greek'] },
      { lang: 'ko', script: ['Hangul'] },
      { lang: 'zh', script: ['Han'] },
      { lang: 'ja', script: ['Hiragana', 'Katakana', 'Han'] },
      { lang: 'he', script: ['Hebrew'] },
      { lang: 'ar', script: ['Arabic'] },
      // { lang: 'bn', script: ['Bengali'] },
      // { lang: 'ti', script: ['Tibetan'] },
    ];

    this.availableFonts = [
      { lang: 'base', font: ['vk-font-100'] },
      { lang: 'cyrillic', font: ['Noto Sans Mono'] },
      { lang: 'el', font: ['Noto Sans Display'] },
      { lang: 'ko', font: ['Noto Sans KR'] },
      { lang: 'zh', font: ['Noto Sans SC', 'Noto Sans TC'] },
      { lang: 'ja', font: ['Noto Sans JP'] },
      { lang: 'he', font: ['Noto Sans Hebrew'] },
      { lang: 'ar', font: ['Noto Sans Arabic'] },
      { lang: 'bn', font: ['Noto Sans Bengali'] },
      { lang: 'ti', font: ['Noto Serif Tibetan'] },
      { lang: 'emoji', font: ['Noto Emoji'] },
    ];

    this.includedFonts = [];

    this.textLayerSettings = null;
    this.canvasLabel = null;

    this.cursorPosition = 0;
    this.cachedText = '';
    this.preservedText = null;
    this.isChangeSizeEvent = false;

    this.dev = false;
    this.timer = null;

    this.init();
  }

  init() {
    this.log(`Init ${this.block.id}`, {
      settings: this.block,
    });

    this.setFonts();
    this.createControls();
    this.createControlTooltips();
    this.controlTextarea();
    this.controlImageUpload();
    // this.controlBgImageUpload();
    this.controlBgImageUploadCrop();
    this.controlQRFields();

    if (this.allowSharing) {
      // remove share button hidden class
      this.shareButton.classList.remove('hidden');
    }
  }

  setFonts() {
    const allowedLanguages = [...this.allowedLanguages];
    if (this.allowEmojis) {
      allowedLanguages.push({ lang: 'emoji' });
    }

    this.includedFonts = this.availableFonts
      .filter((font) => allowedLanguages.some(({ lang }) => lang === font.lang))
      .map(({ font }) => {
        return font.map((f) => (f.includes(' ') ? `'${f}'` : f)).join(',');
      })
      .join(',');
  }

  createControls() {
    const haveMultipleTypes = this.block.options.types && Object.keys(this.block.options.types).length > 1;
    const haveMultipleSizes = this.block.options.sizes && Object.keys(this.block.options.sizes).length > 1;
    const haveMultipleAlignments = this.block.options.alignments && Object.keys(this.block.options.alignments).length > 1;
    const allowPalette = this.block.options.allowPalette || false;

    if (haveMultipleTypes) {
      this.typeController.style.display = 'inherit';
    }
    if (haveMultipleSizes) {
      this.textSizeController.style.display = 'block';
    }
    if (haveMultipleAlignments) {
      this.textAlignController.style.display = 'flex';
    }
    if (this.allowEmojis) {
      this.emojiController.style.display = 'inherit';
    }

    if (allowPalette) {
      this.paletteController.style.display = 'block';
      const debouncedColorChange = this.debounce(this.controlBgColor, 300);
      Coloris({
        alpha: false,
        parent: '.coloris-container',
        el: '.coloris-input',
        clearLabel: 'Clear',
        closeButton: true,
        defaultColor: '',
        onChange: (color) => {
          debouncedColorChange(color);
        },
      });
    }

    if (this.rtlAllowed) {
      this.scriptController.style.display = 'inherit';
    }

    // Create the controls
    // types
    Object.keys(this.block.options.types).forEach((obj) => {
      const type = this.block.options.types[obj];
      let buttonClass = `type-controller__button--${type.name} display-option`;

      if (type?.default) {
        buttonClass += ' selected';
        this.selectedType = type.name;

        // display the default tab
        const selectedTab = this.fieldset.querySelector(`.tab-content[data-type="${type.name}"]`);
        selectedTab.setAttribute('data-active-type', true);
        selectedTab.style.display = 'inherit';

        const currentTypeFields = selectedTab.querySelectorAll('.customizable-field');
        currentTypeFields.forEach((field) => {
          field.setAttribute('required', true);
          field.setAttribute('aria-required', true);
          field.classList.add('required');
        });
      }

      // this.textSizeController.innerHTML += `<button class="${buttonClass}" data-size="${size.name}">${size.displayName}</button>`;
      this.typeController.innerHTML += `<button
        type="button"
        class="${buttonClass}"
        data-type="${type.name}"
        data-is-linked="${String(type.isLinked)}"
        ${type.linkType ? `data-link-type="${type.linkType}"` : ''}
        data-control-title="${type.displayName}"></button>`;
    });

    // sizes
    Object.keys(this.block.options.sizes).forEach((obj) => {
      const size = this.block.options.sizes[obj];
      const button = this.textSizeController.querySelector(`[data-size="${size.name}"]`);
      button.style.display = 'flex';

      if (size?.default) {
        button.classList.add('selected');
        this.selectedSize = size.name;
        this.maxLines = size.maxLines;
        this.lineCharacterLimit = size.lineCharacterLimit;
      }
    });

    // alignments
    Object.keys(this.block.options.alignments).forEach((obj) => {
      const align = this.block.options.alignments[obj];
      const button = this.textAlignController.querySelector(`[data-align="${align.name}"]`);
      button.style.display = 'flex';

      if (align?.default) {
        button.classList.add('selected');

        // fix for selected icon
        const svgContent = button.querySelector('svg').innerHTML;
        const dropdownController = button.closest('.dropdown-controller');
        dropdownController.querySelector('.dropdown-controller__trigger svg').innerHTML = svgContent;
        
        this.selectedAlignment = align.name;
      }
    });

    this.updateDisplay();

    this.dropdownControllers.forEach((controller) => {
      controller.querySelector('.dropdown-controller__trigger').addEventListener('click', () => {
        controller.toggleAttribute('dropdown-controller__open');
      });

      controller.closest('.dropdown-controller').addEventListener('mouseleave', () => {
        controller.removeAttribute('dropdown-controller__open');
      });
    });

    this.typeController.addEventListener('click', this.controlTypes.bind(this));
    this.typeController.addEventListener('forcedClick', this.controlTypes.bind(this));
    this.textSizeController.addEventListener('click', this.controlTextSize.bind(this));
    // this.paletteController.querySelector('[name="bg-color"]').addEventListener('change', this.controlBgColor.bind(this));

    this.textAlignController.addEventListener('click', this.controlTextAlign.bind(this));
    this.scriptController.addEventListener('click', this.controlScript.bind(this));
    this.emojiController.addEventListener('click', this.controlEmojiSelector.bind(this));
    this.emojiController.addEventListener('mouseleave', this.controlEmojiSelectorMouseOut.bind(this));
    this.emojiWrapper.addEventListener('click', this.controlEmojiWrapper.bind(this));
  }

  createControlTooltips() {
    const tooltips = this.querySelectorAll('.vkcf-controls [data-control-title]');
    tooltips.forEach((tooltip) => {
      const tooltipText = tooltip.dataset.controlTitle;
      tooltip.insertAdjacentHTML('beforeend', `<div class="tooltip"><span>${tooltipText}</span></div>`);
    });
  }

  controlTextarea() {
    const textarea = this.textarea;

    this.textarea.addEventListener('input', this.limitCharacters.bind(this));
    this.textarea.addEventListener('blur', (event) => {
      // detect cursor position to insert emojis in the selected position
      // this.cursorPosition = event.target.selectionStart;
      this.cursorPosition = [...event.target.value.slice(0, event.target.selectionStart)].length;
    });

    // focus textarea when clicking on the .textarea-wrapper
    this.fieldset.querySelector('.textarea-wrapper').addEventListener('click', function () {
      textarea.focus();
    });
  }

  removeUnwantedChars(text) {
    // Function to remove emojis from the text
    const removeEmojis = (str) =>
      str.replace(
        /[#*0-9]\uFE0F?\u20E3|[\xA9\xAE\u203C\u2049\u2122\u2139\u2194-\u2199\u21A9\u21AA\u231A\u231B\u2328\u23CF\u23ED-\u23EF\u23F1\u23F2\u23F8-\u23FA\u24C2\u25AA\u25AB\u25B6\u25C0\u25FB\u25FC\u25FE\u2600-\u2604\u260E\u2611\u2614\u2615\u2618\u2620\u2622\u2623\u2626\u262A\u262E\u262F\u2638-\u263A\u2640\u2642\u2648-\u2653\u265F\u2660\u2663\u2665\u2666\u2668\u267B\u267E\u267F\u2692\u2694-\u2697\u2699\u269B\u269C\u26A0\u26A7\u26AA\u26B0\u26B1\u26BD\u26BE\u26C4\u26C8\u26CF\u26D1\u26D3\u26E9\u26F0-\u26F5\u26F7\u26F8\u26FA\u2702\u2708\u2709\u270F\u2712\u2714\u2716\u271D\u2721\u2733\u2734\u2744\u2747\u2757\u2763\u27A1\u2934\u2935\u2B05-\u2B07\u2B1B\u2B1C\u2B55\u3030\u303D\u3297\u3299]\uFE0F?|[\u261D\u270C\u270D](?:\uFE0F|\uD83C[\uDFFB-\uDFFF])?|[\u270A\u270B](?:\uD83C[\uDFFB-\uDFFF])?|[\u23E9-\u23EC\u23F0\u23F3\u25FD\u2693\u26A1\u26AB\u26C5\u26CE\u26D4\u26EA\u26FD\u2705\u2728\u274C\u274E\u2753-\u2755\u2795-\u2797\u27B0\u27BF\u2B50]|\u26F9(?:\uFE0F|\uD83C[\uDFFB-\uDFFF])?(?:\u200D[\u2640\u2642]\uFE0F?)?|\u2764\uFE0F?(?:\u200D(?:\uD83D\uDD25|\uD83E\uDE79))?|\uD83C(?:[\uDC04\uDD70\uDD71\uDD7E\uDD7F\uDE02\uDE37\uDF21\uDF24-\uDF2C\uDF36\uDF7D\uDF96\uDF97\uDF99-\uDF9B\uDF9E\uDF9F\uDFCD\uDFCE\uDFD4-\uDFDF\uDFF5\uDFF7]\uFE0F?|[\uDF85\uDFC2\uDFC7](?:\uD83C[\uDFFB-\uDFFF])?|[\uDFC3\uDFC4\uDFCA](?:\uD83C[\uDFFB-\uDFFF])?(?:\u200D[\u2640\u2642]\uFE0F?)?|[\uDFCB\uDFCC](?:\uFE0F|\uD83C[\uDFFB-\uDFFF])?(?:\u200D[\u2640\u2642]\uFE0F?)?|[\uDCCF\uDD8E\uDD91-\uDD9A\uDE01\uDE1A\uDE2F\uDE32-\uDE36\uDE38-\uDE3A\uDE50\uDE51\uDF00-\uDF20\uDF2D-\uDF35\uDF37-\uDF7C\uDF7E-\uDF84\uDF86-\uDF93\uDFA0-\uDFC1\uDFC5\uDFC6\uDFC8\uDFC9\uDFCF-\uDFD3\uDFE0-\uDFF0\uDFF8-\uDFFF]|\uDDE6\uD83C[\uDDE8-\uDDEC\uDDEE\uDDF1\uDDF2\uDDF4\uDDF6-\uDDFA\uDDFC\uDDFD\uDDFF]|\uDDE7\uD83C[\uDDE6\uDDE7\uDDE9-\uDDEF\uDDF1-\uDDF4\uDDF6-\uDDF9\uDDFB\uDDFC\uDDFE\uDDFF]|\uDDE8\uD83C[\uDDE6\uDDE8\uDDE9\uDDEB-\uDDEE\uDDF0-\uDDF5\uDDF7\uDDFA-\uDDFF]|\uDDE9\uD83C[\uDDEA\uDDEC\uDDEF\uDDF0\uDDF2\uDDF4\uDDFF]|\uDDEA\uD83C[\uDDE6\uDDE8\uDDEA\uDDEC\uDDED\uDDF7-\uDDFA]|\uDDEB\uD83C[\uDDEE-\uDDF0\uDDF2\uDDF4\uDDF7]|\uDDEC\uD83C[\uDDE6\uDDE7\uDDE9-\uDDEE\uDDF1-\uDDF3\uDDF5-\uDDFA\uDDFC\uDDFE]|\uDDED\uD83C[\uDDF0\uDDF2\uDDF3\uDDF7\uDDF9\uDDFA]|\uDDEE\uD83C[\uDDE8-\uDDEA\uDDF1-\uDDF4\uDDF6-\uDDF9]|\uDDEF\uD83C[\uDDEA\uDDF2\uDDF4\uDDF5]|\uDDF0\uD83C[\uDDEA\uDDEC-\uDDEE\uDDF2\uDDF3\uDDF5\uDDF7\uDDFC\uDDFE\uDDFF]|\uDDF1\uD83C[\uDDE6-\uDDE8\uDDEE\uDDF0\uDDF7-\uDDFB\uDDFE]|\uDDF2\uD83C[\uDDE6\uDDE8-\uDDED\uDDF0-\uDDFF]|\uDDF3\uD83C[\uDDE6\uDDE8\uDDEA-\uDDEC\uDDEE\uDDF1\uDDF4\uDDF5\uDDF7\uDDFA\uDDFF]|\uDDF4\uD83C\uDDF2|\uDDF5\uD83C[\uDDE6\uDDEA-\uDDED\uDDF0-\uDDF3\uDDF7-\uDDF9\uDDFC\uDDFE]|\uDDF6\uD83C\uDDE6|\uDDF7\uD83C[\uDDEA\uDDF4\uDDF8\uDDFA\uDDFC]|\uDDF8\uD83C[\uDDE6-\uDDEA\uDDEC-\uDDF4\uDDF7-\uDDF9\uDDFB\uDDFD-\uDDFF]|\uDDF9\uD83C[\uDDE6\uDDE8\uDDE9\uDDEB-\uDDED\uDDEF-\uDDF4\uDDF7\uDDF9\uDDFB\uDDFC\uDDFF]|\uDDFA\uD83C[\uDDE6\uDDEC\uDDF2\uDDF3\uDDF8\uDDFE\uDDFF]|\uDDFB\uD83C[\uDDE6\uDDE8\uDDEA\uDDEC\uDDEE\uDDF3\uDDFA]|\uDDFC\uD83C[\uDDEB\uDDF8]|\uDDFD\uD83C\uDDF0|\uDDFE\uD83C[\uDDEA\uDDF9]|\uDDFF\uD83C[\uDDE6\uDDF2\uDDFC]|\uDFF3\uFE0F?(?:\u200D(?:\u26A7\uFE0F?|\uD83C\uDF08))?|\uDFF4(?:\u200D\u2620\uFE0F?|\uDB40\uDC67\uDB40\uDC62\uDB40(?:\uDC65\uDB40\uDC6E\uDB40\uDC67|\uDC73\uDB40\uDC63\uDB40\uDC74|\uDC77\uDB40\uDC6C\uDB40\uDC73)\uDB40\uDC7F)?)|\uD83D(?:[\uDC08\uDC26](?:\u200D\u2B1B)?|[\uDC3F\uDCFD\uDD49\uDD4A\uDD6F\uDD70\uDD73\uDD76-\uDD79\uDD87\uDD8A-\uDD8D\uDDA5\uDDA8\uDDB1\uDDB2\uDDBC\uDDC2-\uDDC4\uDDD1-\uDDD3\uDDDC-\uDDDE\uDDE1\uDDE3\uDDE8\uDDEF\uDDF3\uDDFA\uDECB\uDECD-\uDECF\uDEE0-\uDEE5\uDEE9\uDEF0\uDEF3]\uFE0F?|[\uDC42\uDC43\uDC46-\uDC50\uDC66\uDC67\uDC6B-\uDC6D\uDC72\uDC74-\uDC76\uDC78\uDC7C\uDC83\uDC85\uDC8F\uDC91\uDCAA\uDD7A\uDD95\uDD96\uDE4C\uDE4F\uDEC0\uDECC](?:\uD83C[\uDFFB-\uDFFF])?|[\uDC6E\uDC70\uDC71\uDC73\uDC77\uDC81\uDC82\uDC86\uDC87\uDE45-\uDE47\uDE4B\uDE4D\uDE4E\uDEA3\uDEB4-\uDEB6](?:\uD83C[\uDFFB-\uDFFF])?(?:\u200D[\u2640\u2642]\uFE0F?)?|[\uDD74\uDD90](?:\uFE0F|\uD83C[\uDFFB-\uDFFF])?|[\uDC00-\uDC07\uDC09-\uDC14\uDC16-\uDC25\uDC27-\uDC3A\uDC3C-\uDC3E\uDC40\uDC44\uDC45\uDC51-\uDC65\uDC6A\uDC79-\uDC7B\uDC7D-\uDC80\uDC84\uDC88-\uDC8E\uDC90\uDC92-\uDCA9\uDCAB-\uDCFC\uDCFF-\uDD3D\uDD4B-\uDD4E\uDD50-\uDD67\uDDA4\uDDFB-\uDE2D\uDE2F-\uDE34\uDE37-\uDE44\uDE48-\uDE4A\uDE80-\uDEA2\uDEA4-\uDEB3\uDEB7-\uDEBF\uDEC1-\uDEC5\uDED0-\uDED2\uDED5-\uDED7\uDEDC-\uDEDF\uDEEB\uDEEC\uDEF4-\uDEFC\uDFE0-\uDFEB\uDFF0]|\uDC15(?:\u200D\uD83E\uDDBA)?|\uDC3B(?:\u200D\u2744\uFE0F?)?|\uDC41\uFE0F?(?:\u200D\uD83D\uDDE8\uFE0F?)?|\uDC68(?:\u200D(?:[\u2695\u2696\u2708]\uFE0F?|\u2764\uFE0F?\u200D\uD83D(?:\uDC8B\u200D\uD83D)?\uDC68|\uD83C[\uDF3E\uDF73\uDF7C\uDF93\uDFA4\uDFA8\uDFEB\uDFED]|\uD83D(?:[\uDC68\uDC69]\u200D\uD83D(?:\uDC66(?:\u200D\uD83D\uDC66)?|\uDC67(?:\u200D\uD83D[\uDC66\uDC67])?)|[\uDCBB\uDCBC\uDD27\uDD2C\uDE80\uDE92]|\uDC66(?:\u200D\uD83D\uDC66)?|\uDC67(?:\u200D\uD83D[\uDC66\uDC67])?)|\uD83E[\uDDAF-\uDDB3\uDDBC\uDDBD])|\uD83C(?:\uDFFB(?:\u200D(?:[\u2695\u2696\u2708]\uFE0F?|\u2764\uFE0F?\u200D\uD83D(?:\uDC8B\u200D\uD83D)?\uDC68\uD83C[\uDFFB-\uDFFF]|\uD83C[\uDF3E\uDF73\uDF7C\uDF93\uDFA4\uDFA8\uDFEB\uDFED]|\uD83D[\uDCBB\uDCBC\uDD27\uDD2C\uDE80\uDE92]|\uD83E(?:[\uDDAF-\uDDB3\uDDBC\uDDBD]|\uDD1D\u200D\uD83D\uDC68\uD83C[\uDFFC-\uDFFF])))?|\uDFFC(?:\u200D(?:[\u2695\u2696\u2708]\uFE0F?|\u2764\uFE0F?\u200D\uD83D(?:\uDC8B\u200D\uD83D)?\uDC68\uD83C[\uDFFB-\uDFFF]|\uD83C[\uDF3E\uDF73\uDF7C\uDF93\uDFA4\uDFA8\uDFEB\uDFED]|\uD83D[\uDCBB\uDCBC\uDD27\uDD2C\uDE80\uDE92]|\uD83E(?:[\uDDAF-\uDDB3\uDDBC\uDDBD]|\uDD1D\u200D\uD83D\uDC68\uD83C[\uDFFB\uDFFD-\uDFFF])))?|\uDFFD(?:\u200D(?:[\u2695\u2696\u2708]\uFE0F?|\u2764\uFE0F?\u200D\uD83D(?:\uDC8B\u200D\uD83D)?\uDC68\uD83C[\uDFFB-\uDFFF]|\uD83C[\uDF3E\uDF73\uDF7C\uDF93\uDFA4\uDFA8\uDFEB\uDFED]|\uD83D[\uDCBB\uDCBC\uDD27\uDD2C\uDE80\uDE92]|\uD83E(?:[\uDDAF-\uDDB3\uDDBC\uDDBD]|\uDD1D\u200D\uD83D\uDC68\uD83C[\uDFFB\uDFFC\uDFFE\uDFFF])))?|\uDFFE(?:\u200D(?:[\u2695\u2696\u2708]\uFE0F?|\u2764\uFE0F?\u200D\uD83D(?:\uDC8B\u200D\uD83D)?\uDC68\uD83C[\uDFFB-\uDFFF]|\uD83C[\uDF3E\uDF73\uDF7C\uDF93\uDFA4\uDFA8\uDFEB\uDFED]|\uD83D[\uDCBB\uDCBC\uDD27\uDD2C\uDE80\uDE92]|\uD83E(?:[\uDDAF-\uDDB3\uDDBC\uDDBD]|\uDD1D\u200D\uD83D\uDC68\uD83C[\uDFFB-\uDFFD\uDFFF])))?|\uDFFF(?:\u200D(?:[\u2695\u2696\u2708]\uFE0F?|\u2764\uFE0F?\u200D\uD83D(?:\uDC8B\u200D\uD83D)?\uDC68\uD83C[\uDFFB-\uDFFF]|\uD83C[\uDF3E\uDF73\uDF7C\uDF93\uDFA4\uDFA8\uDFEB\uDFED]|\uD83D[\uDCBB\uDCBC\uDD27\uDD2C\uDE80\uDE92]|\uD83E(?:[\uDDAF-\uDDB3\uDDBC\uDDBD]|\uDD1D\u200D\uD83D\uDC68\uD83C[\uDFFB-\uDFFE])))?))?|\uDC69(?:\u200D(?:[\u2695\u2696\u2708]\uFE0F?|\u2764\uFE0F?\u200D\uD83D(?:\uDC8B\u200D\uD83D)?[\uDC68\uDC69]|\uD83C[\uDF3E\uDF73\uDF7C\uDF93\uDFA4\uDFA8\uDFEB\uDFED]|\uD83D(?:[\uDCBB\uDCBC\uDD27\uDD2C\uDE80\uDE92]|\uDC66(?:\u200D\uD83D\uDC66)?|\uDC67(?:\u200D\uD83D[\uDC66\uDC67])?|\uDC69\u200D\uD83D(?:\uDC66(?:\u200D\uD83D\uDC66)?|\uDC67(?:\u200D\uD83D[\uDC66\uDC67])?))|\uD83E[\uDDAF-\uDDB3\uDDBC\uDDBD])|\uD83C(?:\uDFFB(?:\u200D(?:[\u2695\u2696\u2708]\uFE0F?|\u2764\uFE0F?\u200D\uD83D(?:[\uDC68\uDC69]|\uDC8B\u200D\uD83D[\uDC68\uDC69])\uD83C[\uDFFB-\uDFFF]|\uD83C[\uDF3E\uDF73\uDF7C\uDF93\uDFA4\uDFA8\uDFEB\uDFED]|\uD83D[\uDCBB\uDCBC\uDD27\uDD2C\uDE80\uDE92]|\uD83E(?:[\uDDAF-\uDDB3\uDDBC\uDDBD]|\uDD1D\u200D\uD83D[\uDC68\uDC69]\uD83C[\uDFFC-\uDFFF])))?|\uDFFC(?:\u200D(?:[\u2695\u2696\u2708]\uFE0F?|\u2764\uFE0F?\u200D\uD83D(?:[\uDC68\uDC69]|\uDC8B\u200D\uD83D[\uDC68\uDC69])\uD83C[\uDFFB-\uDFFF]|\uD83C[\uDF3E\uDF73\uDF7C\uDF93\uDFA4\uDFA8\uDFEB\uDFED]|\uD83D[\uDCBB\uDCBC\uDD27\uDD2C\uDE80\uDE92]|\uD83E(?:[\uDDAF-\uDDB3\uDDBC\uDDBD]|\uDD1D\u200D\uD83D[\uDC68\uDC69]\uD83C[\uDFFB\uDFFD-\uDFFF])))?|\uDFFD(?:\u200D(?:[\u2695\u2696\u2708]\uFE0F?|\u2764\uFE0F?\u200D\uD83D(?:[\uDC68\uDC69]|\uDC8B\u200D\uD83D[\uDC68\uDC69])\uD83C[\uDFFB-\uDFFF]|\uD83C[\uDF3E\uDF73\uDF7C\uDF93\uDFA4\uDFA8\uDFEB\uDFED]|\uD83D[\uDCBB\uDCBC\uDD27\uDD2C\uDE80\uDE92]|\uD83E(?:[\uDDAF-\uDDB3\uDDBC\uDDBD]|\uDD1D\u200D\uD83D[\uDC68\uDC69]\uD83C[\uDFFB\uDFFC\uDFFE\uDFFF])))?|\uDFFE(?:\u200D(?:[\u2695\u2696\u2708]\uFE0F?|\u2764\uFE0F?\u200D\uD83D(?:[\uDC68\uDC69]|\uDC8B\u200D\uD83D[\uDC68\uDC69])\uD83C[\uDFFB-\uDFFF]|\uD83C[\uDF3E\uDF73\uDF7C\uDF93\uDFA4\uDFA8\uDFEB\uDFED]|\uD83D[\uDCBB\uDCBC\uDD27\uDD2C\uDE80\uDE92]|\uD83E(?:[\uDDAF-\uDDB3\uDDBC\uDDBD]|\uDD1D\u200D\uD83D[\uDC68\uDC69]\uD83C[\uDFFB-\uDFFD\uDFFF])))?|\uDFFF(?:\u200D(?:[\u2695\u2696\u2708]\uFE0F?|\u2764\uFE0F?\u200D\uD83D(?:[\uDC68\uDC69]|\uDC8B\u200D\uD83D[\uDC68\uDC69])\uD83C[\uDFFB-\uDFFF]|\uD83C[\uDF3E\uDF73\uDF7C\uDF93\uDFA4\uDFA8\uDFEB\uDFED]|\uD83D[\uDCBB\uDCBC\uDD27\uDD2C\uDE80\uDE92]|\uD83E(?:[\uDDAF-\uDDB3\uDDBC\uDDBD]|\uDD1D\u200D\uD83D[\uDC68\uDC69]\uD83C[\uDFFB-\uDFFE])))?))?|\uDC6F(?:\u200D[\u2640\u2642]\uFE0F?)?|\uDD75(?:\uFE0F|\uD83C[\uDFFB-\uDFFF])?(?:\u200D[\u2640\u2642]\uFE0F?)?|\uDE2E(?:\u200D\uD83D\uDCA8)?|\uDE35(?:\u200D\uD83D\uDCAB)?|\uDE36(?:\u200D\uD83C\uDF2B\uFE0F?)?)|\uD83E(?:[\uDD0C\uDD0F\uDD18-\uDD1F\uDD30-\uDD34\uDD36\uDD77\uDDB5\uDDB6\uDDBB\uDDD2\uDDD3\uDDD5\uDEC3-\uDEC5\uDEF0\uDEF2-\uDEF8](?:\uD83C[\uDFFB-\uDFFF])?|[\uDD26\uDD35\uDD37-\uDD39\uDD3D\uDD3E\uDDB8\uDDB9\uDDCD-\uDDCF\uDDD4\uDDD6-\uDDDD](?:\uD83C[\uDFFB-\uDFFF])?(?:\u200D[\u2640\u2642]\uFE0F?)?|[\uDDDE\uDDDF](?:\u200D[\u2640\u2642]\uFE0F?)?|[\uDD0D\uDD0E\uDD10-\uDD17\uDD20-\uDD25\uDD27-\uDD2F\uDD3A\uDD3F-\uDD45\uDD47-\uDD76\uDD78-\uDDB4\uDDB7\uDDBA\uDDBC-\uDDCC\uDDD0\uDDE0-\uDDFF\uDE70-\uDE7C\uDE80-\uDE88\uDE90-\uDEBD\uDEBF-\uDEC2\uDECE-\uDEDB\uDEE0-\uDEE8]|\uDD3C(?:\u200D[\u2640\u2642]\uFE0F?|\uD83C[\uDFFB-\uDFFF])?|\uDDD1(?:\u200D(?:[\u2695\u2696\u2708]\uFE0F?|\uD83C[\uDF3E\uDF73\uDF7C\uDF84\uDF93\uDFA4\uDFA8\uDFEB\uDFED]|\uD83D[\uDCBB\uDCBC\uDD27\uDD2C\uDE80\uDE92]|\uD83E(?:[\uDDAF-\uDDB3\uDDBC\uDDBD]|\uDD1D\u200D\uD83E\uDDD1))|\uD83C(?:\uDFFB(?:\u200D(?:[\u2695\u2696\u2708]\uFE0F?|\u2764\uFE0F?\u200D(?:\uD83D\uDC8B\u200D)?\uD83E\uDDD1\uD83C[\uDFFC-\uDFFF]|\uD83C[\uDF3E\uDF73\uDF7C\uDF84\uDF93\uDFA4\uDFA8\uDFEB\uDFED]|\uD83D[\uDCBB\uDCBC\uDD27\uDD2C\uDE80\uDE92]|\uD83E(?:[\uDDAF-\uDDB3\uDDBC\uDDBD]|\uDD1D\u200D\uD83E\uDDD1\uD83C[\uDFFB-\uDFFF])))?|\uDFFC(?:\u200D(?:[\u2695\u2696\u2708]\uFE0F?|\u2764\uFE0F?\u200D(?:\uD83D\uDC8B\u200D)?\uD83E\uDDD1\uD83C[\uDFFB\uDFFD-\uDFFF]|\uD83C[\uDF3E\uDF73\uDF7C\uDF84\uDF93\uDFA4\uDFA8\uDFEB\uDFED]|\uD83D[\uDCBB\uDCBC\uDD27\uDD2C\uDE80\uDE92]|\uD83E(?:[\uDDAF-\uDDB3\uDDBC\uDDBD]|\uDD1D\u200D\uD83E\uDDD1\uD83C[\uDFFB-\uDFFF])))?|\uDFFD(?:\u200D(?:[\u2695\u2696\u2708]\uFE0F?|\u2764\uFE0F?\u200D(?:\uD83D\uDC8B\u200D)?\uD83E\uDDD1\uD83C[\uDFFB\uDFFC\uDFFE\uDFFF]|\uD83C[\uDF3E\uDF73\uDF7C\uDF84\uDF93\uDFA4\uDFA8\uDFEB\uDFED]|\uD83D[\uDCBB\uDCBC\uDD27\uDD2C\uDE80\uDE92]|\uD83E(?:[\uDDAF-\uDDB3\uDDBC\uDDBD]|\uDD1D\u200D\uD83E\uDDD1\uD83C[\uDFFB-\uDFFF])))?|\uDFFE(?:\u200D(?:[\u2695\u2696\u2708]\uFE0F?|\u2764\uFE0F?\u200D(?:\uD83D\uDC8B\u200D)?\uD83E\uDDD1\uD83C[\uDFFB-\uDFFD\uDFFF]|\uD83C[\uDF3E\uDF73\uDF7C\uDF84\uDF93\uDFA4\uDFA8\uDFEB\uDFED]|\uD83D[\uDCBB\uDCBC\uDD27\uDD2C\uDE80\uDE92]|\uD83E(?:[\uDDAF-\uDDB3\uDDBC\uDDBD]|\uDD1D\u200D\uD83E\uDDD1\uD83C[\uDFFB-\uDFFF])))?|\uDFFF(?:\u200D(?:[\u2695\u2696\u2708]\uFE0F?|\u2764\uFE0F?\u200D(?:\uD83D\uDC8B\u200D)?\uD83E\uDDD1\uD83C[\uDFFB-\uDFFE]|\uD83C[\uDF3E\uDF73\uDF7C\uDF84\uDF93\uDFA4\uDFA8\uDFEB\uDFED]|\uD83D[\uDCBB\uDCBC\uDD27\uDD2C\uDE80\uDE92]|\uD83E(?:[\uDDAF-\uDDB3\uDDBC\uDDBD]|\uDD1D\u200D\uD83E\uDDD1\uD83C[\uDFFB-\uDFFF])))?))?|\uDEF1(?:\uD83C(?:\uDFFB(?:\u200D\uD83E\uDEF2\uD83C[\uDFFC-\uDFFF])?|\uDFFC(?:\u200D\uD83E\uDEF2\uD83C[\uDFFB\uDFFD-\uDFFF])?|\uDFFD(?:\u200D\uD83E\uDEF2\uD83C[\uDFFB\uDFFC\uDFFE\uDFFF])?|\uDFFE(?:\u200D\uD83E\uDEF2\uD83C[\uDFFB-\uDFFD\uDFFF])?|\uDFFF(?:\u200D\uD83E\uDEF2\uD83C[\uDFFB-\uDFFE])?))?)/g,
        ''
      );

    const removeEmojiToneModifier = (str) => {
      // Use a regular expression to remove emoji tone modifiers
      return str.replace(/\p{Emoji_Modifier}/gu, '');
    };

    // Remove emojis from the input text if emojis are not allowed
    if (!this.allowEmojis) {
      text = removeEmojis(text);
    } else {
      text = removeEmojiToneModifier(text);
    }

    // Define the allowed scripts
    const allowedScripts = this.allowedLanguages.map(({ script }) => {
      return script.map((scriptName) => {
        return `\\p{Script=${scriptName}}`;
      });
    });

    const allowedCharacters = allowedScripts.join('');
    const regexPattern = `[${allowedCharacters}]+`;

    const regex = new RegExp(regexPattern, 'gu');

    const matches = text.match(regex);

    const resultText = matches ? matches.join('') : '';

    return resultText;
  }

  async limitCharacters(event) {
    if (typeof event.inputType === 'undefined' && this.preservedText !== null) {
      event.target.value = this.preservedText;
    } else {
      this.preservedText = null;
    }

    // Regex to remove non-Unicode characters but allow special characters
    event.target.value = this.removeUnwantedChars(event.target.value);

    // Convert to uppercase
    event.target.value = event.target.value.toUpperCase();

    this.preRenderText.innerHTML = event.target.value;

    // Wait for the font to load
    await document.fonts.ready;

    // Prevent writing more than the max number of lines
    while (this.textarea.clientHeight < this.textarea.scrollHeight) {
      event.target.value = event.target.value.substr(0, event.target.value.length - 1);
    }

    // Update the canvas text
    this.cachedText = this.canvas.updateLayerText(this.canvasLabel, event.target.value, this.isChangeSizeEvent, this.emojis);

    // Remove setting just after the text is updated
    if (this.isChangeSizeEvent === true) {
      this.isChangeSizeEvent = false;
    }

    this.textarea.value = this.cachedText;
  }

  controlTypes(event) {
    // do nothing if is already selected
    if (event.target.tagName !== 'BUTTON' || event.target.classList.contains('selected')) {
      return;
    }

    const typeButtons = this.typeController.querySelectorAll('button');
    typeButtons.forEach((button) => {
      if (button.classList.contains('selected')) {
        const previousTabType = button.dataset.type;
        // remove required attribute from the previous tab fields
        const previousTabFields = this.fieldset.querySelectorAll(`.tab-content[data-type="${previousTabType}"] .customizable-field`);
        previousTabFields.forEach(function (field) {
          field.removeAttribute('required');
          field.setAttribute('aria-required', false);
          field.classList.remove('required');
        });

        button.classList.remove('selected');
      }
    });

    const clickedButton = event.target;
    clickedButton.classList.add('selected');

    this.selectedType = clickedButton.dataset.type;

    const tabContents = this.fieldset.querySelectorAll('.tab-content');
    tabContents.forEach(function (tab) {
      tab.style.display = 'none';
      tab.removeAttribute('data-active-type');
    });

    const selectedTab = this.fieldset.querySelector(`.tab-content[data-type="${this.selectedType}"]`);

    // add required attribute to the current tab fields
    const currentTabFields = selectedTab.querySelectorAll('.customizable-field');
    currentTabFields.forEach((field) => {
      field.setAttribute('required', true);
      field.setAttribute('aria-required', true);
      field.classList.add('required');
    });

    selectedTab.style.display = 'inherit';
    selectedTab.setAttribute('data-active-type', true);

    const isLinked = clickedButton.dataset.isLinked;

    // if we have a linked type.. we need to force the click on the linked type to match the same settings
    if (isLinked === 'true' && event.type !== 'forcedClick') {
      const linkType = clickedButton.dataset.linkType ?? this.selectedType;

      // get the closest input-type wrapper
      let allParentElements = document.querySelectorAll('.input-type');
      let parentElementsNotClosest = Array.from(allParentElements).filter((parent) => parent !== clickedButton.closest('.input-type'));

      parentElementsNotClosest.forEach((wrapper) => {
        const sameTypeButtons = wrapper.querySelectorAll(`button[class^="type-controller__button--"][data-type^="${linkType}"]`);

        sameTypeButtons.forEach((typeButton) => {
          // click the button
          // typeButton.click();
          const forcedClick = new CustomEvent('forcedClick', {
            bubbles: true,
            cancelable: true,
          });

          typeButton.dispatchEvent(forcedClick);
        });
      });
    }

    this.canvas.showLayer(this.canvasLabel, this.selectedType);
  }

  controlTextSize(event) {
    const target = event.target;

    // do nothing if is already selected
    if (target.tagName !== 'BUTTON' || target.classList.contains('selected') || target.classList.contains('dropdown-controller__trigger')) {
      return;
    }

    const dropdownController = target.closest('.dropdown-controller');
    dropdownController.removeAttribute('dropdown-controller__open');

    const sizeButtons = this.textSizeController.querySelectorAll('button');
    sizeButtons.forEach(function (button) {
      button.classList.remove('selected');
    });

    const clickedButton = target;
    clickedButton.classList.add('selected');

    this.isChangeSizeEvent = true;

    this.selectedSize = clickedButton.dataset.size;
    this.maxLines = this.block.options.sizes[this.selectedSize].maxLines;
    this.lineCharacterLimit = this.block.options.sizes[this.selectedSize].lineCharacterLimit;

    this.textarea.value = this.textarea.value.trimEnd();
    this.textarea.focus();

    if (this.preservedText === null) {
      this.preservedText = this.cachedText;
      this.textarea.dataset.preserveText = true;
    }

    this.updateDisplay();

    // this.log({
    //   textSize: this.selectedSize,
    //   maxLines: this.maxLines,
    //   lineCharacterLimit: this.lineCharacterLimit
    // });
  }

  controlTextAlign(event) {
    const target = event.target;

    // do nothing if is already selected
    if (target.tagName !== 'BUTTON' || target.classList.contains('selected') || target.classList.contains('dropdown-controller__trigger')) {
      return;
    }

    const dropdownController = target.closest('.dropdown-controller');
    dropdownController.removeAttribute('dropdown-controller__open');

    const alignButtons = this.textAlignController.querySelectorAll('button');
    alignButtons.forEach(function (button) {
      button.classList.remove('selected');
    });

    const clickedButton = target;
    clickedButton.classList.add('selected');

    const svgContent = clickedButton.querySelector('svg').innerHTML;
    dropdownController.querySelector('.dropdown-controller__trigger svg').innerHTML = svgContent;

    this.selectedAlignment = clickedButton.dataset.align;

    this.textarea.focus();
    this.updateDisplay();

    // this.log({textAlign: this.selectedAlignment});
  }

  controlBgColor(color) {
    this.selectedBgColor = color;

    this.textarea.focus();
    this.updateDisplay();
  }

  controlScript(event) {
    // do nothing if is already selected
    if (event.target.tagName !== 'BUTTON' || event.target.classList.contains('selected')) {
      return;
    }

    const scriptButtons = this.scriptController.querySelectorAll('button');
    scriptButtons.forEach(function (button) {
      button.classList.remove('selected');
    });

    const clickedButton = event.target;
    clickedButton.classList.add('selected');

    this.selectedScript = clickedButton.dataset.script;

    // this.selectedAlignment = this.selectedScript === 'rtl' ? 'right' : 'left';
    if (this.selectedScript === 'rtl') {
      this.textAlignController.querySelector('button[data-align="right"]').click();
    } else {
      this.textAlignController.querySelector('button[data-align="left"]').click();
    }

    this.textarea.focus();
    this.updateDisplay();
  }

  controlEmojiSelector() {
    this.emojiController.classList.toggle('emoji-selector--open');
  }

  controlEmojiSelectorMouseOut() {
    this.emojiController.classList.remove('emoji-selector--open');
  }

  controlEmojiWrapper(event) {
    const selectedCharacter = event.target.dataset.character;

    // Insert the character at the cursor position
    const arrayValue = [...this.textarea.value];
    const stringStart = arrayValue.slice(0, this.cursorPosition);
    const stringEnd = arrayValue.slice(this.cursorPosition);

    //const newValue = this.textarea.value.slice(0, this.cursorPosition) + selectedCharacter + this.textarea.value.slice(this.cursorPosition);
    const newValue = [...stringStart, ...selectedCharacter, ...stringEnd];

    this.textarea.value = newValue.join('');

    const customInputEvent = new InputEvent('input', {
      bubbles: true,
      cancelable: true,
      inputType: 'insertText',
      data: selectedCharacter,
    });

    // Set the cursor position to the end of the inserted character (using emoji real length)
    // this.cursorPosition = this.cursorPosition + 1;
    // this.textarea.selectionStart = this.cursorPosition;
    // this.textarea.selectionEnd = this.cursorPosition;
    const newPosition = [...stringStart, ...selectedCharacter].join('').length;
    this.textarea.selectionStart = newPosition;
    this.textarea.selectionEnd = newPosition;

    this.textarea.dispatchEvent(customInputEvent);
    this.textarea.focus();
  }

  updateSVGColor(svg, color) {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(svg, 'image/svg+xml');
    // Traverse the SVG and change all fill and stroke attributes to black
    const elements = xmlDoc.querySelectorAll('[fill], [stroke]');

    for (const element of elements) {
      // check if attribute fill is none
      if (element.hasAttribute('fill') && element.getAttribute('fill') !== 'none') {
        element.setAttribute('fill', color);
      }
      if (element.hasAttribute('stroke') && element.getAttribute('stroke') !== 'none') {
        element.setAttribute('stroke', color);
      }
    }

    // Serialize the modified SVG back to text
    const modifiedSVG = new XMLSerializer().serializeToString(xmlDoc);

    return modifiedSVG;
  }

  controlImageUpload() {
    this.imageUpload.addEventListener('change', (event) => {
      const file = event.target.files[0];

      const reader = new FileReader();
      reader.onload = (event) => {
        const svgText = event.target.result;

        this.canvas.updateLayerImage({
          canvasGroup: this.canvasLabel,
          svgContent: svgText,
          textAlign: this.textLayerSettings.textAlign,
        });
      };

      if (file) {
        reader.readAsText(file);
      }
    });
  }

  controlBgImageUpload() {
    this.bgImageUpload.addEventListener('change', (event) => {
      const file = event.target.files[0];

      const reader = new FileReader();
      reader.onload = (event) => {
        const bgImageFile = event.target.result;

        this.canvas.updateLayerBgImage({
          canvasGroup: this.canvasLabel,
          imageFile: bgImageFile,
        });
      };

      if (file) {
        reader.readAsDataURL(file);
      }
    });
  }

  controlBgImageUploadCrop() {
    this.bgImageUploadCrop.addEventListener('change', (event) => {
      const file = event.target.files[0];

      const reader = new FileReader();
      reader.onload = (event) => {
        const bgImageFile = event.target.result;

        this.handleCropModal(bgImageFile, event.target);
      };

      if (file) {
        reader.readAsDataURL(file);
      }
    });
  }

  handleCropModal(imageFile, inputField) {
    const currentSize = this.block.options.sizes[this.selectedSize].background;

    const canvas = this.canvas;
    const canvasLabel = this.canvasLabel;

    let croppie;
    const modal = new tingle.modal({
      footer: true,
      stickyFooter: false,
      closeMethods: ['overlay', 'button', 'escape'],
      closeLabel: 'Close',
      cssClass: ['custom-class-1', 'custom-class-2'],
      onOpen: function () {
        croppie = new Croppie(document.getElementById('image-cropper'), {
          viewport: { width: currentSize.width, height: currentSize.height, type: 'square' },
          enforceBoundary: false,
          // url: imageFile,
        });

        setTimeout(() => {
          croppie.bind({
            url: imageFile,
            // points: [50, 50, 100, 100],
          });
        }, 300);
      },
      onClose: function () {},
      beforeClose: function () {
        // here's goes some logic
        // e.g. save content before closing the modal
        return true; // close the modal
        // return false; // nothing happens
      },
    });

    modal.setContent(`
      <style>
        .image-cropper-container {position: relative; height: 200px;}
      </style>
      <h3>
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" style="fill: rgba(0, 0, 0, 1);transform: ;msFilter:;"><path d="M19.045 7.401c.378-.378.586-.88.586-1.414s-.208-1.036-.586-1.414l-1.586-1.586c-.378-.378-.88-.586-1.414-.586s-1.036.208-1.413.585L4 13.585V18h4.413L19.045 7.401zm-3-3 1.587 1.585-1.59 1.584-1.586-1.585 1.589-1.584zM6 16v-1.585l7.04-7.018 1.586 1.586L7.587 16H6zm-2 4h16v2H4z"></path></svg>
        Edit your custom keytag!
      </h3>
      <div class="image-cropper-container">
        <div id="image-cropper" />
      </div>
    `);

    // add a button
    const currentField = this.bgImageCropped;
    modal.addFooterBtn('Continue', 'product-form__submit button button--full-width button--secondary', function () {
      croppie
        .result({
          type: 'base64',
          size: 'viewport',
          format: 'png',
          quality: 1,
          circle: false,
        })
        .then(function (base64) {
          // const croppedImageInput = document.getElementById('cropped-image');
          // croppedImageInput.value = base64;

          canvas.updateLayerBgImageCrop({
            canvasGroup: canvasLabel,
            imageFile: base64,
            currentField,
          });

          modal.close();
          modal.destroy();
          croppie.destroy();
        });
    });

    modal.open();
  }

  controlImageUploadworking() {
    this.imageUpload.addEventListener('change', (event) => {
      const file = event.target.files[0];
      const imagePreview = document.createElement('img');

      const reader = new FileReader();
      reader.onload = (event) => {
        imagePreview.src = event.target.result;
        // get width and height
        imagePreview.onload = () => {
          const width = imagePreview.naturalWidth;
          const height = imagePreview.naturalHeight;
          this.canvas.updateLayerImage(this.canvasLabel, { image: imagePreview, width, height }, this.textLayerSettings.textAlign);
        };
      };
      reader.readAsDataURL(file);

      // Remove the previous image
      const imagePreviewContainer = this.imageUpload.nextElementSibling;
      imagePreviewContainer.innerHTML = '';
      // Add the new image
      imagePreviewContainer.appendChild(imagePreview);

      // reader.onload = (event) => {
      //   const img = new Image();
      //   img.onload = () => {
      //     const canvas = document.createElement('canvas');
      //     const ctx = canvas.getContext('2d');
      //     canvas.width = img.width;
      //     canvas.height = img.height;
      //     ctx.drawImage(img, 0, 0);
      //   };
      //   img.src = event.target.result;
      //   this.imageUpload.nextElementSibling.appendChild(img);
      // };

      // reader.readAsDataURL(file);
    });
  }

  async controlImageRender(dataURL, callback) {
    const imagePreview = document.createElement('img');

    const fetchImage = await fetch(dataURL);
    const blob = await fetchImage.blob();

    const reader = new FileReader();
    reader.onload = (event) => {
      imagePreview.src = event.target.result;
      // get width and height
      imagePreview.onload = () => {
        const width = imagePreview.naturalWidth;
        const height = imagePreview.naturalHeight;
        callback(imagePreview, width, height);
      };
    };
    reader.readAsDataURL(blob);
  }

  controlQRFields() {
    const qrField = this.qrUrlField;
    // focus qr field when clicking on the .qr-wrapper
    this.fieldset.querySelector('.qr-wrapper').addEventListener('click', function () {
      qrField.focus();
    });

    this.qrUrlField.addEventListener('input', (event) => {
      const text = event.target.value;

      // Clear timer
      clearTimeout(this.timer);

      // Wait for X ms and then process the request
      this.timer = setTimeout(() => {
        this.generateQRCode({ url: text });
      }, 1000);
    });
  }

  generateQRCode({ url }) {
    let hasError = false;
    let qrConfig = {
      canvasLabel: this.canvasLabel,
      content: false,
    };

    // validate url field to be a valid URL
    const urlRegex = new RegExp(
      '^(https?:\\/\\/)' + // required protocol
        '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
        '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
        '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
        '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
        '(\\#[-a-z\\d_]*)?$', // fragment locator
      'i'
    );

    const phoneRegex = new RegExp(
      // International phone number pattern
      '^\\+?\\d{1,3}[- ]?' + // Optional plus sign and country code
        '\\(?\\d{2,4}\\)?[- ]?' + // Optional area code with parentheses
        '\\d{3,4}[- ]?\\d{3,4}$', // Phone number prefix and suffix
      'i'
    );

    const emailRegex = new RegExp(
      // Email pattern
      '^' + // Start of the string
        '([a-zA-Z0-9._%+-]+@' + // Local part of the email address
        '[a-zA-Z0-9.-]+\\.' + // Domain name part of the email address
        '[a-zA-Z]{2,})' + // Top-level domain part of the email address
        '$', // End of the string
      'i'
    );

    if (!urlRegex.test(url) && !phoneRegex.test(url) && !emailRegex.test(url)) {
      hasError = true;
      this.qrUrlField.setCustomValidity('Please enter a valid phone number, email, or URL.');
    }

    // if the URL is empty, set error
    if (!url) {
      hasError = true;
      this.qrUrlField.setCustomValidity('');
    }

    if (hasError) {
      if (this.qrUrlField.validity.customError) {
        this.qrUrlField.reportValidity();
      }
    } else {
      this.qrUrlField.setCustomValidity('');

      // if the input is a phone number, add the tel: protocol
      if (phoneRegex.test(url)) {
        url = `tel:${url}`;
      }

      // if the input is an email, add the mailto: protocol
      if (emailRegex.test(url)) {
        url = `mailto:${url}`;
      }

      qrConfig = {
        ...qrConfig,
        content: url,
        textAlign: this.textLayerSettings.textAlign,
        width: this.block.options.sizes[this.selectedSize].canvas.height,
      };
    }

    return this.canvas.renderQRcode(qrConfig);
  }

  updateDisplay() {
    // Update the display hidden field
    const size = this.selectedSize.charAt(0).toUpperCase() + this.selectedSize.slice(1);
    const align = this.selectedAlignment.charAt(0).toUpperCase() + this.selectedAlignment.slice(1);
    const direction = this.selectedScript.charAt(0).toUpperCase() + this.selectedScript.slice(1);
    this.block.el.querySelector(`#${this.block.id}-display`).value = `${size} - ${align}`;
    this.block.el.querySelector(`#${this.block.id}-writing-direction`).value = `${direction}`;

    // Update the textarea
    this.textarea.setAttribute('rows', this.maxLines);

    // A value of 0 means the textarea will not be limited by character width
    if (this.lineCharacterLimit === 0) {
      this.textarea.setAttribute('style', `width: 100%; direction: ${this.selectedScript};`);
    } else {
      this.textarea.setAttribute('cols', this.lineCharacterLimit);
      this.textarea.setAttribute('style', `width: ${this.lineCharacterLimit}ch`);
    }

    // include custom fonts
    if (this.block.options.sizes[this.selectedSize].fontFamily) {
      this.includedFonts = `${this.block.options.sizes[this.selectedSize].fontFamily}, ${this.includedFonts}`;
    }

    this.preRenderText.setAttribute('style', `font-family: ${this.includedFonts};`);

    // Create the text layer settings object
    this.textLayerSettings = {
      path: this.block.options.path || null,
      ...this.block.options.sizes[this.selectedSize],
      textAlign: this.selectedAlignment,
      fontFamily: this.includedFonts,
      direction: this.selectedScript,
      watermark: this.watermark,
    };

    // Update the text layer settings
    this.canvasLabel = this.canvas.updateLayerSettings(this.canvasLabel, this.textLayerSettings, this.dev);

    // force textarea input event
    this.textarea.value = this.cachedText;
    this.textarea.dispatchEvent(new Event('input'));
  }

  getBlockSettings(fieldset) {
    const blockId = fieldset.dataset.block;
    let blockSettings = {
      id: blockId,
      el: fieldset,
      options: this.config.options,
    };

    if (this.config.blocks && this.config.blocks[blockId]) {
      blockSettings = this.mergeObjectsRecursive(blockSettings, this.config.blocks[blockId]);
    }

    return blockSettings;
  }

  mergeObjectsRecursive(obj1, obj2) {
    const merged = Object.assign({}, obj1);
    for (const [key, value] of Object.entries(obj2)) {
      if (value === null) {
        delete merged[key];
      } else if (Object.hasOwnProperty.call(obj1, key) && typeof obj1[key] === 'object' && typeof value === 'object') {
        merged[key] = this.mergeObjectsRecursive(obj1[key], value);
      } else {
        merged[key] = value;
      }
    }
    return merged;
  }

  debounce(func, delay) {
    let timeoutId;

    return function (...args) {
      clearTimeout(timeoutId);

      timeoutId = setTimeout(() => {
        func.apply(this, args);
      }, delay);
    };
  }

  log() {
    if (this.dev) {
      // eslint-disable-next-line no-console
      console.log(...arguments);
    }
  }
}

customElements.define('vk-customizable-product-section', VKCustomizableProductSection);
